import { useState } from 'react'

import { Box, Button, useConfirmationDialog } from '@factorialco/deprecated-design-system'
import { CompetencyGroupsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  CompetenciesCountCompetenciesByGroupDocument,
  CompetenciesDeleteCompetencyGroupDocument,
  CompetenciesGroupItemFragment,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EditCompetencyGroupModal } from '../../EditCompetencyGroupModal'

interface Props {
  item: CompetenciesGroupItemFragment
}

export const CompetencyGroupItemActions = ({ item }: Props) => {
  const [showEditModal, setShowEditModal] = useState(false)
  const { openDialog } = useConfirmationDialog()

  const [deleteCompetenciesCompetencyGroup, { loading: deleting }] = useMutation(
    CompetenciesDeleteCompetencyGroupDocument,
    {
      extractorFn: (data) => data.competenciesMutations.deleteCompetenciesCompetencyGroup,
      refetchQueries: [CompetencyGroupsDataTableDocument],
    }
  )

  const { data, loading: loadingCompetenciesTotalCount } = useQuery(
    CompetenciesCountCompetenciesByGroupDocument,
    {
      variables: { competencyGroupId: item.id },
    }
  )
  const competenciesTotalCount = data?.competencies.competenciesConnection.totalCount ?? 0

  const onDelete = async () => {
    const result = await deleteCompetenciesCompetencyGroup({
      variables: { id: item.id },
    })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
      track(trackingEvents.competencies.deleteCompetencyGroup)
    }

    return handled
  }

  return (
    <>
      <Box flexDirection="row" gap="s8">
        <Button
          type="secondary"
          aria-label={i18n.t('competencies_v3.competency_groups_table.edit', {
            competencyGroupName: item.name,
          })}
          iconName="pencil"
          onClick={() => setShowEditModal(true)}
        />
        <Button
          type="secondary"
          aria-label={i18n.t('competencies_v3.competency_groups_table.delete', {
            competencyGroupName: item.name,
          })}
          iconName="delete"
          onClick={() =>
            openDialog({
              illustration: 'delete',
              title: i18n.t('competencies_v3.delete_competency_group.title'),
              description: !!competenciesTotalCount
                ? i18n.t('competencies_v3.delete_competency_group.description_for_assigned')
                : i18n.t('competencies_v3.delete_competency_group.description'),
              onConfirm: onDelete,
            })
          }
          disabled={deleting || loadingCompetenciesTotalCount}
        />
      </Box>

      {showEditModal && (
        <EditCompetencyGroupModal item={item} onClose={() => setShowEditModal(false)} />
      )}
    </>
  )
}
